import { type ReactElement, useCallback, useEffect, useRef } from 'react'

import { isNonNullable } from '@subaio/utils'

import { useRefreshable } from '../useRefreshable'
import { AnimationStatus } from './AnimationStatus'

export { AnimationStatus }

export type AnimationProps = {
	status: AnimationStatus
	duration: number
}

type AnimationRenderBase<T = {}> = {
	content: (key: string, props: AnimationProps & T) => ReactElement
	duration?: number | Duration
}

export type AnimationRender<T = {}> = AnimationRenderBase<T> | AnimationRenderBase<T>['content']

type Duration = { in: number; out: number }

type State = {
	key: string
	status: AnimationStatus
	nextTransition: number
	duration: Duration
	render: AnimationRenderBase['content']
}

type Options = {
	/**
	 * Duration of each animation state. If set to a single number, both in and
	 * out will take the same time. All numbers in seconds.
	 */
	duration: number | Duration
	/** Animate elements on first mount */
	animateInitial?: boolean
	/**
	 * If true, elements added will start as `Initial` state and immediately move to `Entering`.
	 *
	 * If false, elements will start as `Entering`.
	 */
	initialState?: boolean
}

const normalizeDuration = (duration: number | Duration): Duration =>
	typeof duration === 'number' ? { in: duration, out: duration } : duration

const normalizeRender = (
	value: AnimationRender,
	durationBase: Duration,
): { render: AnimationRenderBase['content']; duration: Duration } =>
	typeof value === 'function'
		? { render: value, duration: durationBase }
		: { render: value.content, duration: value.duration ? normalizeDuration(value.duration) : durationBase }

/**
 * Helper function for injecting props into an animation render function/object.
 */
export const injectAnimationRenderProps = <T extends object>(render: AnimationRender<T>, props: T): AnimationRender =>
	typeof render === 'function'
		? (key, defaultProps) => render(key, { ...defaultProps, ...props })
		: { duration: render.duration, content: (key, defaultProps) => render.content(key, { ...defaultProps, ...props }) }

/**
 * Manage animation state for a list of elements, based on keys. The elements
 * will start in either `Initial` or `Entering` (depending on the `initialState`
 * option) and move to `Active`. When later rendering, and omitting the key, the
 * last provided render method will be called until the out-animation is
 * complete.
 *
 * Duration of animation can be customized by supplying a duration on the render
 * object for each entry or as a global value passed to the hook. The `duration`
 * provided to the render function should always be used for animations, for
 * consistency.
 *
 * @example
 * ```ts
 * const Component = () => {
 *     const keyedAnimation = useKeyedAnimation({ duration: 0.2 })
 *
 *     return (
 *         <div>
 *             {keyedAnimation(entries.map((entry) => (
 *                 [entry.id, (key, { duration, status }) => <Entry key={key} duration={duration} status={status} {...entry} />]
 *             )))}
 *         </div>
 *     )
 * }
 * ```
 */
export const useKeyedAnimation = ({
	duration: durationBaseRaw,
	animateInitial = false,
	initialState: useInitialState = false,
}: Options) => {
	const { refresh } = useRefreshable()

	const shouldUpdateRef = useRef(false)
	const statesRef = useRef<Array<State>>()
	const timeoutRef = useRef<{ id: Timeout; time: number } | undefined>()

	const updateState = useCallback(() => {
		// Handle case of never rendering
		if (!statesRef.current) return

		// Update states if necessary
		const now = Date.now()
		let hasUpdated = false
		statesRef.current = statesRef.current
			.map((state) => {
				switch (state.status) {
					case AnimationStatus.Active:
						return state
					case AnimationStatus.Initial:
						hasUpdated = true

						return {
							...state,
							status: AnimationStatus.Entering,
						}
					case AnimationStatus.Entering: {
						if (state.nextTransition > now) return state

						hasUpdated = true

						return {
							...state,
							status: AnimationStatus.Active,
							nextTransition: Number.POSITIVE_INFINITY,
						}
					}
					case AnimationStatus.Exiting: {
						if (state.nextTransition > now) return state

						hasUpdated = true

						return undefined
					}
					default:
						return undefined
				}
			})
			.filter(isNonNullable)

		// Rerender if anything changed
		if (hasUpdated) {
			refresh()
		}

		// Check if a timer is necessary
		const transitioningStates = statesRef.current.filter(
			({ status }) => status === AnimationStatus.Entering || status === AnimationStatus.Exiting,
		)
		if (transitioningStates.length === 0) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current.id)
				timeoutRef.current = undefined
			}
			return
		}
		// Find next transition time
		const time = transitioningStates.reduce(
			(current, { nextTransition }) => Math.min(current, nextTransition),
			Number.POSITIVE_INFINITY,
		)

		// Timer already set correctly
		if (timeoutRef.current?.time === time) return

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current.id)
		}

		const duration = time - Date.now()

		const id = setTimeout(() => {
			timeoutRef.current = undefined
			updateState()
		}, duration)

		timeoutRef.current = { id, time }
	}, [refresh])

	useEffect(() => {
		if (!shouldUpdateRef.current) return
		shouldUpdateRef.current = false
		updateState()
	})

	// Add cleanup of timeout
	useEffect(
		() => () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current.id)
			}
		},
		[],
	)

	let hasCalled = false

	return (
		entries: Array<[string, AnimationRender] | false | null> | Record<string, AnimationRender | false | null>,
	) => {
		if (hasCalled) {
			throw new Error('Cannot use the same animation group multiple times')
		}
		hasCalled = true
		const durationBase = normalizeDuration(durationBaseRaw)
		const entriesNormalized = (Array.isArray(entries) ? entries : Object.entries(entries)).filter(
			(entry): entry is [string, AnimationRender] => Boolean(entry),
		)

		const now = Date.now()

		// Should new elements animate in
		if (statesRef.current || animateInitial) {
			const currentStates = statesRef.current ?? []
			const updatedStates: Array<State> = []
			let addBuffer: Array<State> = []
			let last = -1
			let hasUpdated = false

			const getDeletedStates = (end: number) =>
				currentStates.slice(last + 1, end).map((state) => ({
					...state,
					status: AnimationStatus.Exiting,
					nextTransition: now + state.duration.out * 1000,
				}))

			for (const [key, renderRaw] of entriesNormalized) {
				const { render, duration: durationRaw } = normalizeRender(renderRaw, durationBase)
				// Look forwards from the last matched element
				let index = last
				do {
					index++
				} while (index < currentStates.length && currentStates[index].key !== key)

				// If a match wasn't found, this is a new element
				if (index === currentStates.length) {
					// Check if the element has just been moved to a previous position
					const moveIndex = updatedStates.findIndex((state) => state.key === key)
					if (moveIndex === -1) {
						const duration = normalizeDuration(durationRaw ?? durationBase)
						// New entry added
						addBuffer.push({
							key,
							render,
							status: useInitialState ? AnimationStatus.Initial : AnimationStatus.Entering,
							nextTransition: now + duration.in * 1000,
							duration,
						})
					} else {
						// Key already exists elsewhere (unless there's duplicate keys)
						// Remove deleting element, since it wasn't actually deleted
						updatedStates.splice(moveIndex, 1)

						addBuffer.push({
							// Get current animation status
							...currentStates.find((state) => state.key === key)!,
							render,
						})
					}
				} else {
					// If a match was found, deleted and added entries from the last
					// All previous skipped entries should be deleted
					const deletedStates = getDeletedStates(index)
					// Add deleted and added entries before this one
					updatedStates.push(...deletedStates, ...addBuffer, {
						...currentStates[index],
						render,
					})
					hasUpdated ||= deletedStates.length > 0 || addBuffer.length > 0
					addBuffer = []
					last = index
				}
			}
			// Manage trailing deleted/added entries
			const deletedStates = getDeletedStates(currentStates.length)
			updatedStates.push(...deletedStates, ...addBuffer)
			hasUpdated ||= deletedStates.length > 0 || addBuffer.length > 0

			statesRef.current = updatedStates
			if (hasUpdated) {
				shouldUpdateRef.current = true
			}
		} else {
			// If initial render and `animateInitial` is false, just add everything as Active
			statesRef.current = entriesNormalized.map(([key, render]) => ({
				key,
				status: AnimationStatus.Active,
				nextTransition: Number.POSITIVE_INFINITY,
				...normalizeRender(render, durationBase),
			}))
		}

		return statesRef.current.map(({ key, status, render, duration }) =>
			render(key, { status, duration: status === AnimationStatus.Exiting ? duration.out : duration.in }),
		)
	}
}
